import { Validators } from '@angular/forms';
import {
  transformBookingData,
  transformEwrcData,
} from './calculation-transforms';
import { CalculationConfig, CalculationType } from './calculation.types';
import { dateBetweenValidatorFn } from './calculation.utils';

/**
 * 공통 일자구분 옵션 (4종)
 */
const DATE_TYPE_OPTIONS_4 = [
  { value: 'StartDate', label: 'Departure Date' },
  { value: 'ReturnDate', label: 'Return Date' },
  { value: 'CreateDate', label: 'Booking Date' },
  { value: 'UpdateDate', label: 'Edit Date' },
];

/**
 * 일자구분 옵션 (5종, settlement 전용)
 */
const DATE_TYPE_OPTIONS_5 = [
  ...DATE_TYPE_OPTIONS_4.slice(0, 3),
  { value: 'CompleteDate', label: 'completeDt' },
  { value: 'CancelDate', label: 'cancelDt' },
];

/**
 * 공통 예약 상태 기본값 (hidden)
 */
const BOOKING_STATE_CODE_DEFAULT = [
  'RECEIVED',
  'ESTIMATE_REQUESTED',
  'ESTIMATE_COMPLETED',
  'ESTIMATE_SENT',
  'ARRANGEMENT_REQUESTED',
  'ARRANGING',
  'PAYMENT_REQ',
  'CONFIRMED',
  'COMPLETED',
];

/**
 * 정산 도메인 Config 정의
 */
export const CALCULATION_CONFIGS: Record<CalculationType, CalculationConfig> = {
  // 정산 (EWRC 인당)
  ewrc: {
    key: 'ewrc',
    title: 'brandFn.menu.calculation',
    routePath: 'calculation',
    permission: 'CALCULATION$READ',
    searchFields: [
      {
        name: 'dateType',
        type: 'date-type',
        label: 'Date Type',
        defaultValue: 'StartDate',
        options: DATE_TYPE_OPTIONS_4,
      },
      {
        name: 'fromDate',
        type: 'date-from',
        label: 'Date From',
        required: true,
      },
      {
        name: 'toDate',
        type: 'date-to',
        label: 'Date To',
        required: true,
        validators: [dateBetweenValidatorFn(31)],
      },
      {
        name: 'packagegoodsIdIn',
        type: 'package-single',
        label: 'Package',
        required: true,
      },
      {
        name: 'bookingStateCodeIn',
        type: 'hidden',
        defaultValue: BOOKING_STATE_CODE_DEFAULT,
      },
    ],
    tableComponent: () =>
      import(
        '../components/dumb/calculation-table/calculation-table.component'
      ).then((m) => m.CalculationTableComponent),
    apiService: 'BookingApi',
    apiListMethod: 'getBookingCalcList',
    dataTransformFn: transformEwrcData,
    features: {},
  },

  // 예약별 정산
  booking: {
    key: 'booking',
    title: 'brandFn.menu.calculation',
    routePath: 'calculation/booking2',
    permission: 'CALCULATION$READ',
    searchFields: [
      {
        name: 'dateType',
        type: 'date-type',
        label: 'Date Type',
        defaultValue: 'StartDate',
        options: DATE_TYPE_OPTIONS_4,
      },
      {
        name: 'fromDate',
        type: 'date-from',
        label: 'Date From',
        required: true,
      },
      {
        name: 'toDate',
        type: 'date-to',
        label: 'Date To',
        required: true,
        validators: [dateBetweenValidatorFn(31)],
      },
      {
        name: 'packagegoodsIdIn',
        type: 'package-multiple',
        label: 'Package',
        validators: [Validators.minLength(1)],
      },
      {
        name: 'bookingStateCodeIn',
        type: 'hidden',
        defaultValue: BOOKING_STATE_CODE_DEFAULT,
      },
    ],
    tableComponent: () =>
      import(
        '../components/dumb/calculation-booking-table/calculation-booking-table.component'
      ).then((m) => m.CalculationBookingTableComponent),
    apiService: 'BookingApi',
    apiListMethod: 'getBookingCalcList',
    dataTransformFn: transformBookingData,
    features: {
      hasExcelDownload: true,
      excelApiMethod: 'getCalculationBookingXlsx',
    },
  },

  // 패키지별 정산
  packagegoods: {
    key: 'packagegoods',
    title: 'brandFn.menu.calculation',
    routePath: 'calculation/packagegoods',
    permission: 'CALCULATION$READ',
    searchFields: [
      {
        name: 'dateType',
        type: 'date-type',
        label: 'Date Type',
        defaultValue: 'StartDate',
        options: DATE_TYPE_OPTIONS_4,
      },
      {
        name: 'fromDate',
        type: 'date-from',
        label: 'Date From',
        required: true,
      },
      {
        name: 'toDate',
        type: 'date-to',
        label: 'Date To',
        required: true,
        validators: [dateBetweenValidatorFn(31)],
      },
      {
        name: 'packagegoodsIdIn',
        type: 'package-single',
        label: 'Package',
        required: true,
      },
      {
        name: 'bookingStateCodeIn',
        type: 'hidden',
        defaultValue: BOOKING_STATE_CODE_DEFAULT,
      },
    ],
    tableComponent: () =>
      import(
        '../components/dumb/calculation-packagegoods-table/calculation-packagegoods-table.component'
      ).then((m) => m.CalculationPackagegoodsTableComponent),
    apiService: 'BookingApi',
    apiListMethod: 'getBookingCalcByPackagegoodsList',
    features: {},
  },

  // 예약 정산
  settlement: {
    key: 'settlement',
    title: 'brandFn.menu.calculation',
    routePath: 'calculation/booking',
    permission: 'CALCULATION$READ',
    searchFields: [
      {
        name: 'bookingSettlementStatus',
        type: 'status-select',
        label: 'status',
        defaultValue: 'INCOMPLETE',
        options: [
          { value: 'INCOMPLETE', label: 'bookingSettlementStatus.INCOMPLETE' },
          { value: 'COMPLETE', label: 'bookingSettlementStatus.COMPLETE' },
          { value: 'CANCEL', label: 'bookingSettlementStatus.CANCEL' },
        ],
      },
      {
        name: 'dateType',
        type: 'date-type',
        label: 'Date Type',
        defaultValue: 'StartDate',
        options: DATE_TYPE_OPTIONS_5,
      },
      {
        name: 'fromDate',
        type: 'date-from',
        label: 'Date From',
        required: true,
      },
      {
        name: 'toDate',
        type: 'date-to',
        label: 'Date To',
        required: true,
      },
      {
        name: 'tradeCodeIn',
        type: 'codemaster-multi',
        label: 'tradeCode',
        featureGuard: 'booking_tradeCode',
        codemasterType: 'Company',
      },
      {
        name: 'packagegoodsIdIn',
        type: 'package-multiple',
        label: 'Package',
      },
    ],
    tableComponent: () =>
      import(
        '../components/dumb/booking-settlement-table/booking-settlement-table.component'
      ).then((m) => m.BookingSettlementTableComponent),
    apiService: 'BookingSettlementApi',
    apiListMethod: 'getList',
    features: {
      hasCheckbox: true,
      hasBatchComplete: true,
      hasBatchCancel: true,
      hasSettlementReset: true,
      needsCurrencyCdList: true,
    },
  },
};
